import { useState } from 'react';
import PropTypes from 'prop-types';
import IncidentCard from '../IncidentCard';
import { 
  calculateRiskScore, 
  calculateTrend, 
  calculateOverallPosture, 
  getPostureLabel, 
  getPostureColor 
} from '@/utils/riskScoring';

/**
 * Executive Summary View Component for SOC Dashboard
 * Displays high-level overview of all security metrics
 * 
 * @param {Object} props - Component props
 * @param {Object} props.data - Summary data containing all metrics
 * @param {Object} props.previousData - Previous period data for trend calculation
 */
const ExecutiveSummaryView = ({ data, previousData }) => {
  // Initialize fallback timestamp once
  const [fallbackTimestamp] = useState(() => Date.now());
  
  if (!data) {
    return (
      <div className="soc-view executive-summary-view">
        <h2 className="view-title">Executive Summary</h2>
        <p className="no-data-message">Loading summary data...</p>
      </div>
    );
  }

  // Calculate risk scores for different categories
  const dlpRiskScore = calculateRiskScore({
    fileCount: data.totalDLPIncidents || 0,
    dataVolumeMB: data.dlpDataVolume || 0
  });

  const insiderRiskScore = calculateRiskScore({
    fileCount: data.totalInsiderRiskIncidents || 0,
    offHoursActivityCount: data.insiderOffHoursCount || 0,
    failedAttempts: data.insiderFailedAttempts || 0
  });

  const intuneRiskScore = calculateRiskScore({
    fileCount: (data.intuneNonCompliantDeviceCount || 0) + (data.intuneThreatenedDeviceCount || 0)
  });

  const entraRiskScore = calculateRiskScore({
    fileCount: (data.entraRiskyUserCount || 0) + (data.entraRiskySignInCount || 0),
    failedAttempts: data.entraFailedSignIns || 0,
    uniqueIPCount: data.entraUniqueRiskyIPs || 0
  });

  // Calculate trends if previous data is available
  const dlpTrend = previousData ? calculateTrend(
    data.totalDLPIncidents || 0,
    previousData.totalDLPIncidents || 0
  ) : null;

  const insiderTrend = previousData ? calculateTrend(
    data.totalInsiderRiskIncidents || 0,
    previousData.totalInsiderRiskIncidents || 0
  ) : null;

  const intuneTrend = previousData ? calculateTrend(
    (data.intuneNonCompliantDeviceCount || 0) + (data.intuneThreatenedDeviceCount || 0),
    (previousData.intuneNonCompliantDeviceCount || 0) + (previousData.intuneThreatenedDeviceCount || 0)
  ) : null;

  const entraTrend = previousData ? calculateTrend(
    (data.entraRiskyUserCount || 0) + (data.entraRiskySignInCount || 0),
    (previousData.entraRiskyUserCount || 0) + (previousData.entraRiskySignInCount || 0)
  ) : null;

  // Calculate overall security posture score using centralized utility
  const overallScore = calculateOverallPosture([
    dlpRiskScore, 
    insiderRiskScore, 
    intuneRiskScore, 
    entraRiskScore
  ]);

  return (
    <div className="soc-view executive-summary-view">
      <h2 className="view-title">Security Operations Center - Executive Summary</h2>

      {/* Overall Security Posture */}
      <div className="overall-posture-card">
        <h3>Overall Security Posture</h3>
        <div className="posture-score" style={{ color: getPostureColor(overallScore) }}>
          <span className="score-value">{overallScore}</span>
          <span className="score-label">{getPostureLabel(overallScore)}</span>
        </div>
        <div className="posture-timestamp">
          Last Updated: {new Date(data.reportGeneratedAt || fallbackTimestamp).toLocaleString()}
        </div>
      </div>

      {/* Key Metrics Grid */}
      <div className="summary-metrics-grid">
        <IncidentCard
          title="Data Loss Prevention"
          riskScore={dlpRiskScore}
          data={{
            count: data.totalDLPIncidents || 0,
            fileExfiltration: data.dlpFileExfiltrationCount || 0,
            unusualSharing: data.dlpUnusualSharingCount || 0,
            largeTransfers: data.dlpLargeTransfersCount || 0
          }}
          incidentType="DLP"
          trend={dlpTrend}
          expandable={true}
        />

        <IncidentCard
          title="Insider Risk Indicators"
          riskScore={insiderRiskScore}
          data={{
            count: data.totalInsiderRiskIncidents || 0,
            anomalousAccess: data.insiderAnomalousAccessCount || 0,
            privilegeEscalation: data.insiderPrivilegeEscalationCount || 0,
            offHoursActivity: data.insiderOffHoursCount || 0,
            suspiciousDownloads: data.insiderSuspiciousDownloadsCount || 0
          }}
          incidentType="InsiderRisk"
          trend={insiderTrend}
          expandable={true}
        />

        <IncidentCard
          title="Intune Device Security"
          riskScore={intuneRiskScore}
          data={{
            count: (data.intuneNonCompliantDeviceCount || 0) + (data.intuneThreatenedDeviceCount || 0),
            nonCompliant: data.intuneNonCompliantDeviceCount || 0,
            threatened: data.intuneThreatenedDeviceCount || 0
          }}
          incidentType="Intune"
          trend={intuneTrend}
          expandable={true}
        />

        <IncidentCard
          title="Entra ID (Azure AD) Security"
          riskScore={entraRiskScore}
          data={{
            count: (data.entraRiskyUserCount || 0) + (data.entraRiskySignInCount || 0),
            riskyUsers: data.entraRiskyUserCount || 0,
            riskySignIns: data.entraRiskySignInCount || 0,
            anomalousLogons: data.entraAnomalousLogonCount || 0
          }}
          incidentType="EntraID"
          trend={entraTrend}
          expandable={true}
        />
      </div>

      {/* Quick Stats Bar */}
      <div className="quick-stats-bar">
        <div className="stat-item">
          <span className="stat-label">Total Incidents</span>
          <span className="stat-value">
            {(data.totalDLPIncidents || 0) + 
             (data.totalInsiderRiskIncidents || 0) +
             ((data.intuneNonCompliantDeviceCount || 0) + (data.intuneThreatenedDeviceCount || 0)) +
             ((data.entraRiskyUserCount || 0) + (data.entraRiskySignInCount || 0))}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">High Priority</span>
          <span className="stat-value high-priority">
            {[dlpRiskScore, insiderRiskScore, intuneRiskScore, entraRiskScore].filter(score => score >= 40).length}
          </span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Time Range</span>
          <span className="stat-value">{data.timeRangeDays || 30} days</span>
        </div>
      </div>
    </div>
  );
};

ExecutiveSummaryView.propTypes = {
  data: PropTypes.object,
  previousData: PropTypes.object
};

ExecutiveSummaryView.defaultProps = {
  data: null,
  previousData: null
};

export default ExecutiveSummaryView;
